import { useCallback } from "react";
import { UseFormReturn } from "react-hook-form";
import { OfferFormValues } from "../schemas/offerFormSchema";
import { useVariationUtils } from "./useVariationUtils";
import { CampLocation } from "@/types/campLocation";
import { DecimalEnumMap } from "@/types/decimalEnum";
import { sessionLinkDefault } from "@/functions-shared/offer/sessionLinkDefaults";

interface UseProgramFieldManagementProps {
  form: UseFormReturn<OfferFormValues>;
  verifiedLocation: CampLocation | null;
  enumsMap: Map<string, any>;
  resetFieldModified: (fieldName: string) => void;
}

/**
 * Hook to manage program field values, resets, and comparisons
 */
export const useProgramFieldManagement = ({
  form,
  verifiedLocation,
  enumsMap,
  resetFieldModified
}: UseProgramFieldManagementProps) => {
  // Use variation utilities with verified published location
  const { resetFieldToProgram: baseResetFieldToProgram, isEqualToProgramValue: baseIsEqualToProgramValue } = useVariationUtils(
    verifiedLocation, 
    form, 
    { usePublishedData: true } // Always use published data
  );

  // Helper function to find matching alternative pricing based on current numDaysOverride
  const findMatchingAlternative = useCallback((variation: any, numDaysOverride: number | null) => {
    if (!variation?.regularOfferIncrementAlternatives || numDaysOverride === null || numDaysOverride === undefined) {
      return null;
    }
    
    for (const alternative of variation.regularOfferIncrementAlternatives) {
      const enumItem = enumsMap.get(alternative.key);
      if (enumItem && enumItem.value === numDaysOverride) {
        return alternative;
      }
    }
    
    return null;
  }, [enumsMap]);

  // ENHANCED: Wrap resetFieldToProgram to reset to effective program values (base OR alternative)
  const resetFieldToProgram = useCallback((fieldName: string) => {
    const variation = form.getValues("variation");
    const numDaysOverride = form.getValues("numDaysOverride");
    
    if (fieldName === "priceOverride") {
      // Find current effective program price (base or alternative)
      const matchingAlternative = findMatchingAlternative(variation, numDaysOverride);
      const effectiveProgramPrice = matchingAlternative 
        ? matchingAlternative.price 
        : variation?.priceRegular || null;
      
      
      form.setValue("priceOverride", effectiveProgramPrice);
      resetFieldModified(fieldName);
    }
    else if (fieldName === "priceNoteOverride") {
      // Find current effective program note (base or alternative)
      const matchingAlternative = findMatchingAlternative(variation, numDaysOverride);
      const effectiveProgramNote = matchingAlternative 
        ? (matchingAlternative.note || "") 
        : (variation?.priceNote || "");
      
      
      form.setValue("priceNoteOverride", effectiveProgramNote);
      resetFieldModified(fieldName);
    }
    else if (fieldName === "pricingTierPriceOverrides.tier2") {
      const matchingAlternative = findMatchingAlternative(variation, numDaysOverride);
      const effectiveProgramTier2Price = matchingAlternative
        ? (matchingAlternative.priceTier2 ?? null)
        : (variation?.priceRegularTier2 ?? null);

      // Ensure the map exists before setting nested fields
      const currentMap = form.getValues("pricingTierPriceOverrides");
      if (currentMap == null) {
        form.setValue("pricingTierPriceOverrides", { v: 1 } as any);
      }

      form.setValue("pricingTierPriceOverrides.tier2" as any, effectiveProgramTier2Price);
      resetFieldModified(fieldName);
    }
    else if (fieldName === "pricingTierPriceOverrides.tier3") {
      const matchingAlternative = findMatchingAlternative(variation, numDaysOverride);
      const effectiveProgramTier3Price = matchingAlternative
        ? (matchingAlternative.priceTier3 ?? null)
        : (variation?.priceRegularTier3 ?? null);

      const currentMap = form.getValues("pricingTierPriceOverrides");
      if (currentMap == null) {
        form.setValue("pricingTierPriceOverrides", { v: 1 } as any);
      }

      form.setValue("pricingTierPriceOverrides.tier3" as any, effectiveProgramTier3Price);
      resetFieldModified(fieldName);
    }
    else if (fieldName === "timingNoteOverride") {
      // Timing notes don't have alternatives, always use base program value
      const programValue = variation?.timings?.timingNote || "";
      form.setValue("timingNoteOverride", programValue);
      resetFieldModified(fieldName);
    } 
    else if (fieldName === "description") {
      // Reset to program's description value
      const programValue = variation?.description || "";
      form.setValue("description", programValue);
      resetFieldModified(fieldName);
    } 
    else if (fieldName === "eligibilityNotes") {
      // Reset to program's eligibilityNotes value
      const programValue = variation?.eligibilityNotes || "";
      form.setValue("eligibilityNotes", programValue);
      resetFieldModified(fieldName);
    } 
    else if (fieldName === "url" || fieldName === "regUrl") {
      // KB-4351 — Session website resets to defaultSessionUrl, never to the program page.
      form.setValue(fieldName, sessionLinkDefault(variation, fieldName));
      resetFieldModified(fieldName);
    }
    else {
      // Use the base implementation for other fields (like numDaysOverride)
      baseResetFieldToProgram(fieldName, resetFieldModified, enumsMap);
    }
  }, [form, baseResetFieldToProgram, resetFieldModified, enumsMap, findMatchingAlternative]);
  
  // ENHANCED: isEqualToProgramValue to handle alternative pricing
  const isEqualToProgramValue = useCallback((fieldName: string, currentValue: any) => {
    const variation = form.getValues("variation");
    const numDaysOverride = form.getValues("numDaysOverride");
    
    // NEW: age override parity
    if (fieldName === "minAgeInYearsOverride") {
      return currentValue === (variation?.minAgeInYears ?? null);
    }
    if (fieldName === "maxAgeInYearsOverride") {
      return currentValue === (variation?.maxAgeInYears ?? null);
    }
    if (fieldName === "minAgeGradeKeyOverride") {
      return (currentValue || "") === (variation?.minAgeGradeKey || "");
    }
    if (fieldName === "maxAgeGradeKeyOverride") {
      return (currentValue || "") === (variation?.maxAgeGradeKey || "");
    }
    if (fieldName === "minAgeNoteOverride") {
      return (currentValue || "") === (variation?.minAgeNote || "");
    }
    if (fieldName === "maxAgeNoteOverride") {
      return (currentValue || "") === (variation?.maxAgeNote || "");
    }
    if (fieldName === "showMinAgeGradeAsPrimaryOverride") {
      return !!currentValue === !!variation?.showMinAgeGradeAsPrimary;
    }
    if (fieldName === "showMaxAgeGradeAsPrimaryOverride") {
      return !!currentValue === !!variation?.showMaxAgeGradeAsPrimary;
    }

    if (fieldName === "priceOverride") {
      // Check against base program price
      if (currentValue === variation?.priceRegular) {
        return true;
      }
      
      // CRITICAL FIX: Also check against alternative pricing
      if (variation?.regularOfferIncrementAlternatives && enumsMap && numDaysOverride) {
        for (const alternative of variation.regularOfferIncrementAlternatives) {
          const enumItem = enumsMap.get(alternative.key);
          if (enumItem && enumItem.value === numDaysOverride && currentValue === alternative.price) {
            return true;
          }
        }
      }
      
      return false;
    }
    else if (fieldName === "pricingTierPriceOverrides.tier2") {
      const baseTier2 = variation?.priceRegularTier2 ?? null;
      if (currentValue === baseTier2) return true;

      const matchingAlternative = findMatchingAlternative(variation, numDaysOverride);
      if (matchingAlternative) {
        const altTier2 = matchingAlternative.priceTier2 ?? null;
        if (currentValue === altTier2) return true;
      }

      return false;
    }
    else if (fieldName === "pricingTierPriceOverrides.tier3") {
      const baseTier3 = variation?.priceRegularTier3 ?? null;
      if (currentValue === baseTier3) return true;

      const matchingAlternative = findMatchingAlternative(variation, numDaysOverride);
      if (matchingAlternative) {
        const altTier3 = matchingAlternative.priceTier3 ?? null;
        if (currentValue === altTier3) return true;
      }

      return false;
    }
    else if (fieldName === "priceNoteOverride") {
      // Check against base program note
      const baseProgramNote = variation?.priceNote || "";
      if (currentValue === baseProgramNote) {
        return true;
      }
      
      // CRITICAL FIX: Also check against alternative pricing notes
      if (variation?.regularOfferIncrementAlternatives && enumsMap && numDaysOverride) {
        for (const alternative of variation.regularOfferIncrementAlternatives) {
          const enumItem = enumsMap.get(alternative.key);
          const alternativeNote = alternative.note || "";
          if (enumItem && enumItem.value === numDaysOverride && currentValue === alternativeNote) {
            return true;
          }
        }
      }
      
      return false;
    }
    else if (fieldName === "timingNoteOverride") {
      // Compare with program's timingNote value (empty string is NOT the same as null)
      const programValue = variation?.timings?.timingNote || "";
      return currentValue === programValue;
    } 
    else if (fieldName === "description") {
      // Compare with program's description value
      const programValue = variation?.description || "";
      return currentValue === programValue;
    } 
    else if (fieldName === "eligibilityNotes") {
      // Compare with program's eligibilityNotes value
      const programValue = variation?.eligibilityNotes || "";
      return currentValue === programValue;
    } 
    else if (fieldName === "url" || fieldName === "regUrl") {
      return currentValue === sessionLinkDefault(variation, fieldName);
    }
    else {
      // Use the base implementation for other fields
      return baseIsEqualToProgramValue(fieldName, currentValue, enumsMap);
    }
  }, [form, baseIsEqualToProgramValue, enumsMap]);
  
  return {
    resetFieldToProgram,
    isEqualToProgramValue
  };
};
